/**
 * Admin Context Sagas
 * Handles async operations for admin context: fetching the tenants list.
 */

import { call, put, takeLatest } from '@ufc/core'
import { getHttpClient } from '@ufc/core'
import { adminContextActions } from './admin-context.slice'
import type { TenantSummary } from '../domain/types/admin-context.types'

// ============================================================================
// Helper Functions
// ============================================================================

function mapResponseToTenantSummary(item: Record<string, unknown>): TenantSummary {
  return {
    id: String(item.id),
    documentId: String(item.documentId ?? item.id),
    code: String(item.code ?? ''),
    name: String(item.name ?? ''),
    status: (item.tenantStatus ?? 'inactive') as TenantSummary['status'],
    channel: (item.channel ?? 'b2b') as TenantSummary['channel'],
  }
}

// ============================================================================
// Worker Sagas
// ============================================================================

function* fetchTenantsListSaga() {
  try {
    const http = getHttpClient()

    const response: { data: unknown } = yield call([http, http.get], '/api/tenants', {
      params: {
        'filters[tenantStatus][$ne]': 'deleted',
        'sort': 'name:asc',
        'pagination[pageSize]': 200,
      },
    })

    const responseData = response.data as Record<string, unknown>
    const raw = Array.isArray(responseData?.data)
      ? (responseData.data as Record<string, unknown>[])
      : []

    const tenants: TenantSummary[] = raw.map(mapResponseToTenantSummary)

    yield put(adminContextActions.fetchTenantsListSuccess(tenants))
  } catch {
    yield put(adminContextActions.fetchTenantsListFailure())
  }
}

// ============================================================================
// Watcher Saga
// ============================================================================

export function* adminContextSaga() {
  yield takeLatest(adminContextActions.fetchTenantsList.type, fetchTenantsListSaga)
}
